import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DELIVERY_OPTIONS, isNoDocketMode, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScanDispatch = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [entityData, setEntityData] = useState(null);
  const [entityType, setEntityType] = useState('member'); // 'member' or 'advertiser'
  const [courierInfo, setCourierInfo] = useState({ type: '', tracking_url: '' });
  const [specialRemark, setSpecialRemark] = useState(''); // Persistent special remark
  
  const getCurrentMonth = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[new Date().getMonth()];
  };

  const [formData, setFormData] = useState({
    member_id: '',
    name: '',
    docket_no: '',
    magazine: '',
    courier_type: '',  // Trackon / DTDC / Registered Post / By Hand / By Post / E-Magazine
    month: getCurrentMonth(),
    year: new Date().getFullYear().toString()
  });
  // When By Hand / By Post / E-Magazine is picked, docket is irrelevant.
  const noDocketMode = isNoDocketMode(formData.courier_type);

  useEffect(() => {
    if (formData.member_id) {
      fetchEntityDetails(formData.member_id);
    }
  }, [formData.member_id, entityType]);

  useEffect(() => {
    if (formData.docket_no) {
      detectCourierType(formData.docket_no);
    }
  }, [formData.docket_no]);

  const fetchEntityDetails = async (id) => {
    try {
      // Auto-detect magazine based on ID prefix
      let detectedMagazine = '';
      if (id.startsWith('ATMREN') || id.startsWith('ATMEEN') || id.startsWith('ATENADV') || id.startsWith('ATMCEN')) {
        detectedMagazine = 'Aabkari Times ENG';
      } else if (id.startsWith('AT')) {
        detectedMagazine = 'Aabkari Times';
      } else if (id.startsWith('ST')) {
        detectedMagazine = 'Sugar Times';
      }
      
      // Try member first
      let response;
      let type = 'member';
      
      try {
        response = await axios.get(`${API}/members/${id}`);
      } catch {
        // If member not found, try advertiser
        response = await axios.get(`${API}/advertisers/${id}`);
        type = 'advertiser';
      }
      
      setEntityData(response.data);
      setEntityType(type);
      setFormData(prev => ({
        ...prev,
        name: response.data.name,
        magazine: detectedMagazine || response.data.database_type || response.data.magazine
      }));
      toast.success(`${type === 'member' ? 'Member' : 'Advertiser'} details loaded`);
    } catch (error) {
      console.error('Error fetching details:', error);
      setEntityData(null);
      if (formData.member_id) {
        toast.error('ID not found in members or advertisers');
      }
    }
  };

  const detectCourierType = async (docketNo) => {
    try {
      // Validation: Check if docket number starts with AT or ST
      const docketUpper = docketNo.trim().toUpperCase();
      if (docketUpper.startsWith('AT') || docketUpper.startsWith('ST')) {
        toast.error('Invalid docket number! Please check - it looks like a Member/Advertiser ID (starts with AT/ST)');
        setCourierInfo({ type: '', tracking_url: '' });
        return;
      }

      const response = await axios.get(`${API}/courier-type/${docketNo}`);
      const detected = response.data.courier_type;
      setCourierInfo({
        type: detected,
        tracking_url: response.data.tracking_url
      });
      // Auto-fill courier_type dropdown if user hasn't picked one yet
      if (detected && !formData.courier_type) {
        const canonical = deliveryLabel(detected);
        setFormData(prev => ({ ...prev, courier_type: canonical }));
      }
    } catch (error) {
      console.error('Error detecting courier type:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // When By Hand / By Post / E-Magazine chosen, docket is NOT required.
    const noDocket = isNoDocketMode(formData.courier_type);
    if (!formData.member_id || !formData.name || !formData.magazine || !formData.month ||
        (!noDocket && !formData.docket_no) || !formData.courier_type) {
      toast.error(noDocket ? 'Please fill in all fields' : 'Please fill in all fields including docket number');
      return;
    }

    // Validation: Check if docket number starts with AT or ST (only when docket is used)
    if (!noDocket) {
      const docketUpper = formData.docket_no.trim().toUpperCase();
      if (docketUpper.startsWith('AT') || docketUpper.startsWith('ST')) {
        toast.error('Invalid docket number! Please check - it looks like a Member/Advertiser ID (starts with AT/ST)');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Build payload: when noDocket, auto-fill docket with courier code (BH / BP / EMAG)
      const noDocket = isNoDocketMode(formData.courier_type);
      const autoDocket = noDocket
        ? (formData.courier_type === 'By Hand' ? 'BH' :
           formData.courier_type === 'By Post' ? 'BP' : 'EMAG')
        : formData.docket_no;
      const dispatchData = {
        ...formData,
        docket_no: autoDocket,
        special_remark: specialRemark.trim() || ''
      };
      await axios.post(`${API}/dispatches`, dispatchData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Dispatch recorded successfully');
      
      // Reset form for next entry, but KEEP month, year, and special remark
      const currentMonth = formData.month;
      const currentYear = formData.year;
      
      setFormData({
        member_id: '',
        name: '',
        docket_no: '',
        magazine: '',
        courier_type: '',
        month: currentMonth, // Keep selected month
        year: currentYear    // Keep selected year
      });
      setEntityData(null);
      setCourierInfo({ type: '', tracking_url: '' });
      // Note: specialRemark is NOT reset - it persists until manually cleared
    } catch (error) {
      console.error('Error creating dispatch:', error);
      toast.error('Failed to record dispatch');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="scan-dispatch-title">
            Scan Dispatch
          </h1>
        </div>

        <div className="form-section mb-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>
            <Camera className="inline mr-2" size={20} />
            Scan QR Code or Enter Member ID
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Member ID *</Label>
              <Input
                data-testid="member-id-input"
                value={formData.member_id}
                onChange={(e) => setFormData({ ...formData, member_id: e.target.value.trim() })}
                placeholder="Enter or scan member ID"
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Scan the QR code from the printed label or enter the Member ID manually.
              </p>
            </div>
          </div>
        </div>

        {entityData && (
          <div className="form-section mb-8" data-testid="entity-details">
            <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>
              {entityType === 'member' ? 'Member' : 'Advertiser'} Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600">Name</Label>
                <p className="font-semibold">{entityData.name}</p>
              </div>
              <div>
                <Label className="text-gray-600">Company</Label>
                <p className="font-semibold">{entityData.company_name}</p>
              </div>
              <div>
                <Label className="text-gray-600">Designation</Label>
                <p className="font-semibold">{entityData.designation}</p>
              </div>
              <div>
                <Label className="text-gray-600">Contact</Label>
                <p className="font-semibold">{entityData.contact_no}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-section">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>
            Dispatch Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Courier Type *</Label>
              <Select
                value={deliveryLabel(formData.courier_type)}
                onValueChange={(value) => setFormData({ ...formData, courier_type: value })}
              >
                <SelectTrigger data-testid="courier-type-select">
                  <SelectValue placeholder="Select courier" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {noDocketMode && (
                <p className="text-xs text-gray-500">No docket needed for {formData.courier_type}</p>
              )}
            </div>

            {!noDocketMode && (
              <div className="space-y-2">
                <Label>Docket Number *</Label>
                <Input
                  data-testid="docket-no-input"
                  value={formData.docket_no}
                  onChange={(e) => setFormData({ ...formData, docket_no: e.target.value })}
                  placeholder="Enter or scan docket number"
                  required
                />
                {courierInfo.type && (
                  <p className="text-sm text-green-600 font-semibold">
                    Auto-detected: {courierInfo.type}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Magazine *</Label>
              <Select
                value={formData.magazine}
                onValueChange={(value) => setFormData({ ...formData, magazine: value })}
                disabled={!!entityData}
              >
                <SelectTrigger data-testid="magazine-select" disabled={!!entityData}>
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
              {entityData && formData.magazine && (
                <p className="text-xs text-gray-500">Auto-detected based on ID prefix</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Month *</Label>
              <Select
                value={formData.month}
                onValueChange={(value) => setFormData({ ...formData, month: value })}
              >
                <SelectTrigger data-testid="month-select">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Year *</Label>
              <Select
                value={formData.year}
                onValueChange={(value) => setFormData({ ...formData, year: value })}
              >
                <SelectTrigger data-testid="year-select">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Special Remark (persists until cleared)</Label>
                {specialRemark && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSpecialRemark('')}
                    className="text-red-500 hover:text-red-700"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Input
                data-testid="special-remark-input"
                value={specialRemark}
                onChange={(e) => setSpecialRemark(e.target.value)}
                placeholder="e.g., Bill created, Complimentary copy, etc."
              />
              {specialRemark && (
                <p className="text-xs text-blue-600">
                  ✓ This remark will be added to all subsequent dispatches until cleared
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading} className="btn-primary" data-testid="submit-dispatch-button">
              <Save className="mr-2" size={18} />
              {loading ? 'Recording...' : 'Record Dispatch'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScanDispatch;